import type { TOC } from '@ember/component/template-only';
import { concat } from '@ember/helper';
import {
  PrismBanner,
  PrismButton,
  PrismInput,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import { pageTitle } from 'ember-page-title';
import { and, eq, not } from 'ember-truth-helpers';
import ButtonSingleSignOn from 'frontend-login/components/button-single-sign-on';
import LoadingScreen from 'frontend-login/components/loading-screen';
import type RegisterController from 'frontend-login/controllers/register';
import type RegisterRoute from 'frontend-login/routes/register';
import type { ModelFrom } from 'frontend-login/utils/routes';
import { contextualPageTitle } from 'frontend-utils';
import { LoginCard, LoginCardFooter, PasswordValidation } from 'password-form';

import styles from './register.module.css';

interface RegisterSignature {
  controller: RegisterController;
  model: ModelFrom<RegisterRoute>;
}

<template>
  {{pageTitle (contextualPageTitle (t "login.sign_up"))}}

  {{#if
    (and
      @controller.sharedAuth.isRedirecting
      (not @controller.sharedAuth.loadingButtonType)
    )
  }}
    <LoadingScreen
      @isBeingRedirected={{@controller.sharedAuth.isBeingRedirected}}
    />
  {{else}}
    <LoginCard @title={{t "login.welcome_user"}}>
      <div class="mt-600 flex flex-col">
        {{#if @controller.sharedAuth.showError}}
          <div class="mb-600">
            <PrismBanner
              @bannerMessage={{@controller.sharedAuth.errorMessage}}
              @bannerStyle="critical"
              @bannerTitle={{@controller.sharedAuth.errorTitle}}
              @icon="warning"
              @link={{@controller.sharedAuth.errorLink}}
              data-test-id="banner-error"
            />
          </div>
        {{/if}}
        <div class="flex flex-col gap-400">
          <PrismInput
            @autofocus={{true}}
            @disabled={{true}}
            @label={{t "login.email"}}
            @name="email"
            @removePadding={{true}}
            @type="email"
            @value={{@controller.sharedAuth.emailInputValue}}
            data-test-id="input-email"
          />

          {{#if
            (and
              (not @model.ssoEnforced)
              @controller.sharedAuth.passwordLoginOption
            )
          }}
            <PrismInput
              @label={{t "login.method.create_password"}}
              @onInput={{@controller.sharedAuth.setPassword}}
              @removePadding={{true}}
              @required={{true}}
              @requiredErrorMessage={{t "login.error.password"}}
              @type="password"
              @value={{@controller.sharedAuth.password}}
              data-test-id="input-create-password"
            />
            {{#unless @controller.arePasswordsValid}}
              <PasswordValidation
                @newPassword={{@controller.sharedAuth.password}}
                @setNewPasswordValid={{@controller.setNewPasswordValidState}}
              />
            {{/unless}}
            <PrismInput
              @errorMessage={{t "login.error.password_mismatch"}}
              @hasErrors={{not @controller.isRepeatPasswordValid}}
              @label={{t "login.method.repeat_password"}}
              @onInput={{@controller.setRepeatPassword}}
              @removePadding={{true}}
              @required={{true}}
              @requiredErrorMessage={{t "login.error.password"}}
              @type="password"
              @value={{@controller.repeatPassword}}
              data-test-id="input-repeat-password"
            />

          {{/if}}
        </div>

        <PrismButton
          @isDisabled={{@controller.isSubmitButtonDisabled}}
          @label={{@controller.submitButtonLabel}}
          @onClick={{@controller.submitForm}}
          class="mt-400"
          data-test-id="button-submit"
        />
        {{#if @model.ssoEnforced}}
          <p class="text-200--regular mt-400 flex justify-center gap-100">
            {{t "login.sso_required"}}
            <a
              class="prism-link"
              href="https://help.mylighthouse.com/articles/9059340-guide-to-single-sign-on-sso"
              rel="noopener noreferrer"
              target="_blank"
            >{{t "general.learn_more"}}</a>
          </p>
        {{else}}
          <p
            class={{concat
              "text-200--regular"
              " "
              styles.text-between-dividers
              " "
              "mt-600 flex items-center justify-center"
            }}
          >
            {{t "login.or_sign_up_with"}}
          </p>
          <div class="mt-300 flex flex-col gap-200">
            {{#each
              @controller.sharedAuth.defaultSsoLoginOptions
              as |ssoLoginOption|
            }}
              <ButtonSingleSignOn
                @isLoading={{eq
                  @controller.sharedAuth.loadingButtonType
                  ssoLoginOption.gipType
                }}
                @onClick={{ssoLoginOption.onClickAction}}
                @type={{ssoLoginOption.type}}
                data-test-id="button-sso"
              />
            {{/each}}
          </div>
        {{/if}}

        {{#unless @model.ssoEnforced}}
          <LoginCardFooter
            @isRegistrationFlow={{true}}
            @showSignUpForTrial={{true}}
          />
        {{/unless}}
      </div>
    </LoginCard>
  {{/if}}
</template> satisfies TOC<RegisterSignature>;
